import { useEffect, useState, useMemo } from "react";
import api from "../../utils/api";
import { toast } from "sonner";
import { ArrowLeft, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const AdminApplications = () => {
  const navigate = useNavigate();

   const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  // 🔍 SEARCH + FILTER
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | verified | unverified

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await api.get("/applications");
      setApplications(res.data);
    } catch {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const verifyApplication = async (id) => {
    try {
      await api.put(`/applications/${id}/verify`);
      toast.success("Application verified");
      fetchApplications();
    } catch {
      toast.error("Verification failed");
    }
  };

  const openDeletePopup = (app) => {
    setSelectedApp(app);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/applications/${selectedApp.id}`);
      toast.success("Application deleted");
      setDeleteModalOpen(false);
      setSelectedApp(null);
      fetchApplications();
    } catch {
      toast.error("Delete failed");
    }
  };

  // 🔍 FILTERED DATA
  const filteredApplications = useMemo(() => {
    return applications.filter((app) => {
      const matchesSearch =
        app.name.toLowerCase().includes(search.toLowerCase()) ||
        app.email.toLowerCase().includes(search.toLowerCase()) ||
        app.phone.includes(search);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "verified" && app.verified) ||
        (statusFilter === "unverified" && !app.verified);

      return matchesSearch && matchesStatus;
    });
  }, [applications, search, statusFilter]);

  // 📥 EXPORT CSV
  const exportCSV = () => {
    const headers = [
      "Name",
      "Email",
      "Phone",
      "Type",
      "Verified",
      "Resume URL",
      "Date",
    ];

    const rows = filteredApplications.map((a) => [
      a.name,
      a.email,
      a.phone,
      a.application_type,
      a.verified ? "Yes" : "No",
      a.resume_url,
      new Date(a.created_at).toLocaleDateString(),
    ]);

    let csv = headers.join(",") + "\n";
    rows.forEach((row) => {
      csv += row.map((v) => `"${v}"`).join(",") + "\n";
    });

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "applications.csv";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="p-10">Loading applications...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
     <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-heading font-bold text-gradient">CodeSky Admin</h1>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-slate-600 hover:text-primary transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </nav>
       
       <div className="max-w-7xl mx-auto px-6 pt-8 pb-10">
        {/* 🔙 Back Button */}
<div className="mb-4">
  <button
    onClick={() => navigate(-1)}
    className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors"
  >
    <ArrowLeft className="w-4 h-4" />
    <span className="text-sm font-medium">Back</span>
  </button>
</div>

        <h1 className="text-3xl font-bold mb-6">
          Job & Internship Applications
        </h1>

        {/* 🔍 SEARCH + FILTER + EXPORT */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Input
            placeholder="Search name, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />

          <select
            className="border rounded px-3 py-2"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>

          <Button variant="outline" onClick={exportCSV}>
            Export CSV
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Resume</th>
                  <th>Date</th>
                  <th className="text-right pr-4">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="border-t hover:bg-slate-50">
                    <td className="p-4 font-medium">{app.name}</td>
                    <td>{app.email}</td>
                    <td>{app.phone}</td>
                    <td className="capitalize">{app.application_type}</td>

                    <td>
                      {app.verified ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                          Verified
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700">
                          Unverified
                        </span>
                      )}
                    </td>

                    <td>
                      <a
                        href={app.resume_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline"
                      >
                        View Resume
                      </a>
                    </td>

                    <td>
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>

                    <td className="text-right pr-4 space-x-2">
                      {!app.verified && (
                        <button
                          onClick={() => verifyApplication(app.id)}
                          className="px-3 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700"
                        >
                          Verify
                        </button>
                      )}

                      <button
                        onClick={() => openDeletePopup(app)}
                        className="px-3 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* DELETE POPUP */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Application?</DialogTitle>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-4">
              <p>
                Delete application of{" "}
                <strong>{selectedApp.name}</strong>?
              </p>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminApplications;
